import { useCallback, useEffect, useMemo, useRef } from 'react';
import _useWebSocket, { ReadyState } from 'react-use-websocket';

import {
  WebSocketConfig,
  WebSocketConnectionOptions,
  WebSocketMessage,
  WebSocketMessageType,
} from '@fly/shared';
import {
  addEvent,
  addMessage,
  removeConnection,
  selectConnectionById,
  setConnectionState,
  useAppDispatch,
  useAppSelector,
} from '@fly/store';
import log from 'loglevel';

// Vite 8 CJS interop: default import from CJS modules with __esModule may be
// the entire module.exports object instead of module.exports.default
const useWebSocket: typeof _useWebSocket =
  typeof _useWebSocket === 'function'
    ? _useWebSocket
    : 'default' in (_useWebSocket as object)
      ? (_useWebSocket as any).default
      : (_useWebSocket as typeof _useWebSocket);
if (typeof useWebSocket !== 'function') {
  throw new Error(
    `react-use-websocket resolved to ${typeof useWebSocket} — CJS interop shim needs updating`
  );
}

interface UseWebSocketOptions extends WebSocketConnectionOptions {
  config?: Partial<WebSocketConfig>;
  autoConnect?: boolean;
  connectionId?: string;
}

interface UseWebSocketReturn {
  sendMessage: (data: any) => void;
  sendJsonMessage: (data: any) => void;
  lastMessage: WebSocketMessage | null;
  readyState: ReadyState;
  isConnected: boolean;
  isConnecting: boolean;
  isReconnecting: boolean;
  error: string | null;
  connect: () => void;
  disconnect: () => void;
  connectionId: string;
}

export const useWebSocketConnection = (
  url: string,
  options: UseWebSocketOptions = {}
): UseWebSocketReturn => {
  const dispatch = useAppDispatch();
  const {
    config = {},
    autoConnect = true,
    connectionId: providedConnectionId,
    onMessage,
    onConnect,
    onDisconnect,
    onError,
    onReconnect,
  } = options;

  // Generate connection ID if not provided
  const connectionId = useMemo(() => {
    return (
      providedConnectionId ||
      `ws_${url.replace(/[^a-zA-Z0-9]/g, '_')}_${Date.now()}`
    );
  }, [providedConnectionId, url]);

  // Get connection state from Redux
  const connectionState = useAppSelector((state) =>
    selectConnectionById(state, connectionId)
  );

  // Create message ID generator
  const messageIdRef = useRef(0);
  const generateMessageId = useCallback(() => {
    return `${connectionId}_${++messageIdRef.current}`;
  }, [connectionId]);

  // WebSocket configuration
  const wsConfig = useMemo(
    () => ({
      url,
      autoConnect,
      reconnectAttempts: config.reconnectAttempts || 5,
      reconnectInterval: config.reconnectInterval || 1000,
      heartbeatInterval: config.heartbeatInterval || 30000,
      messageTimeout: config.messageTimeout || 5000,
      ...config,
    }),
    [url, autoConnect, config]
  );

  // Setup WebSocket connection
  const {
    sendMessage: wsSendMessage,
    sendJsonMessage: wsSendJsonMessage,
    lastMessage: wsLastMessage,
    readyState,
    getWebSocket,
  } = useWebSocket(url, {
    onOpen: () => {
      log.log(`WebSocket connected: ${connectionId}`);
      dispatch(
        setConnectionState({
          connectionId,
          connectionState: {
            isConnected: true,
            isConnecting: false,
            isReconnecting: false,
            lastConnectedAt: Date.now(),
            error: undefined,
          },
        })
      );

      dispatch(
        addEvent({
          type: 'connect',
          timestamp: Date.now(),
        })
      );

      onConnect?.();
    },
    onClose: (event) => {
      log.log(`WebSocket disconnected: ${connectionId}`, event);
      dispatch(
        setConnectionState({
          connectionId,
          connectionState: {
            isConnected: false,
            isConnecting: false,
            isReconnecting: false,
            lastDisconnectedAt: Date.now(),
          },
        })
      );

      dispatch(
        addEvent({
          type: 'disconnect',
          timestamp: Date.now(),
          data: event,
        })
      );

      onDisconnect?.();
    },
    onError: (error) => {
      log.error(`WebSocket error: ${connectionId}`, error);
      dispatch(
        setConnectionState({
          connectionId,
          connectionState: {
            isConnected: false,
            isConnecting: false,
            isReconnecting: false,
            error: 'WebSocket error',
          },
        })
      );

      dispatch(
        addEvent({
          type: 'error',
          timestamp: Date.now(),
          data: error,
        })
      );

      onError?.(error);
    },
    onMessage: (event) => {
      try {
        const data = JSON.parse(event.data);
        const payload = data.payload ?? null;
        const message: WebSocketMessage = {
          id: generateMessageId(),
          type: data.type || 'unknown',
          data: data.data || data,
          payload,
          timestamp: Date.now(),
          source: connectionId,
        };

        dispatch(addMessage(message));
        onMessage?.(message);

        // Handle heartbeat
        if (message.type === WebSocketMessageType.HEARTBEAT) {
          sendJsonMessage({
            type: WebSocketMessageType.HEARTBEAT,
            data: { pong: true },
          });
        }
      } catch (error) {
        log.error('Failed to parse WebSocket message:', error);
        const errorMessage: WebSocketMessage = {
          id: generateMessageId(),
          type: WebSocketMessageType.ERROR,
          data: { error: 'Failed to parse message', originalData: event.data },
          timestamp: Date.now(),
          source: connectionId,
        };
        dispatch(addMessage(errorMessage));
      }
    },
    shouldReconnect: (closeEvent) => {
      const shouldReconnect = closeEvent.code !== 1000; // Don't reconnect on normal closure
      if (shouldReconnect) {
        dispatch(
          setConnectionState({
            connectionId,
            connectionState: {
              isReconnecting: true,
              reconnectAttempts: (connectionState?.reconnectAttempts || 0) + 1,
            },
          })
        );

        dispatch(
          addEvent({
            type: 'reconnect',
            timestamp: Date.now(),
            data: { attempt: connectionState?.reconnectAttempts || 0 },
          })
        );

        onReconnect?.(connectionState?.reconnectAttempts || 0);
      }
      return shouldReconnect;
    },
    reconnectInterval: wsConfig.reconnectInterval,
    reconnectAttempts: wsConfig.reconnectAttempts,
  });

  // Enhanced send functions with message tracking
  const sendMessage = useCallback(
    (data: any) => {
      const message: WebSocketMessage = {
        id: generateMessageId(),
        type: WebSocketMessageType.OUTGOING,
        data,
        timestamp: Date.now(),
        source: connectionId,
      };

      dispatch(addMessage(message));
      wsSendMessage(data);
    },
    [wsSendMessage, generateMessageId, dispatch, connectionId]
  );

  const sendJsonMessage = useCallback(
    (data: any) => {
      const message: WebSocketMessage = {
        id: generateMessageId(),
        type: WebSocketMessageType.OUTGOING,
        data,
        timestamp: Date.now(),
        source: connectionId,
      };

      dispatch(addMessage(message));
      wsSendJsonMessage(data);
    },
    [wsSendJsonMessage, generateMessageId, dispatch, connectionId]
  );

  // Connection management
  const connect = useCallback(() => {
    const ws = getWebSocket();
    if (ws && ws.readyState === WebSocket.CLOSED) {
      dispatch(
        setConnectionState({
          connectionId,
          connectionState: { isConnecting: true },
        })
      );
      ws.close();
    }
  }, [getWebSocket, dispatch, connectionId]);

  const disconnect = useCallback(() => {
    const ws = getWebSocket();
    if (ws) {
      ws.close(1000, 'User initiated disconnect');
    }
  }, [getWebSocket]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      dispatch(removeConnection(connectionId));
    };
  }, [dispatch, connectionId]);

  // Parse last message
  const lastMessage = useMemo(() => {
    if (!wsLastMessage) return null;

    try {
      const data = JSON.parse(wsLastMessage.data);
      return {
        id: generateMessageId(),
        type: data.type || 'unknown',
        data: data.data || data,
        payload: data.payload ?? null,
        timestamp: Date.now(),
        source: connectionId,
      } as WebSocketMessage;
    } catch {
      return null;
    }
  }, [wsLastMessage, generateMessageId, connectionId]);

  return {
    sendMessage,
    sendJsonMessage,
    lastMessage,
    readyState,
    isConnected: readyState === ReadyState.OPEN,
    isConnecting: readyState === ReadyState.CONNECTING,
    isReconnecting: connectionState?.isReconnecting || false,
    error: connectionState?.error || null,
    connect,
    disconnect,
    connectionId,
  };
};
